/**
 * Input Component - Reusable input with variants
 *
 * Variants:
 * - default: Standard input
 * - filled: Filled background
 * - outline: Outlined input
 *
 * Sizes:
 * - sm: Small
 * - md: Medium (default)
 * - lg: Large
 */

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/shared/lib/utils';

const inputVariants = cva(
  'flex w-full rounded-md border transition-colors duration-200 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-neutral-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'border-neutral-300 bg-white focus-visible:border-primary-500 focus-visible:ring-primary-500',
        filled: 'border-transparent bg-neutral-100 focus-visible:border-primary-500 focus-visible:ring-primary-500 focus-visible:bg-white',
        outline: 'border-2 border-neutral-300 bg-transparent focus-visible:border-primary-500 focus-visible:ring-primary-500',
      },
      size: {
        sm: 'h-8 px-3 py-1 text-xs',
        md: 'h-9 px-3 py-1 text-sm',
        lg: 'h-10 px-4 py-2 text-base',
      },
      state: {
        default: '',
        error: 'border-error-500 focus-visible:border-error-500 focus-visible:ring-error-500',
        success: 'border-success-500 focus-visible:border-success-500 focus-visible:ring-success-500',
        warning: 'border-warning-500 focus-visible:border-warning-500 focus-visible:ring-warning-500',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      state: 'default',
    },
  }
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  leftElement?: React.ReactNode;
  rightElement?: React.ReactNode;
  containerClassName?: string;
}

/**
 * Input Component
 *
 * @example
 * ```tsx
 * <Input placeholder="Enter text" />
 * <Input label="Email" type="email" />
 * <Input variant="filled" size="lg" />
 * <Input error="This field is required" />
 * <Input leftIcon={<SearchIcon />} />
 * <Input rightElement={<Button>Search</Button>} />
 * ```
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, variant, size, state, label, error, helperText, leftIcon, rightIcon, leftElement, rightElement, containerClassName, id, ...props }, ref) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    const errorMessage = error || (state === 'error' ? 'This field has an error' : '');
    const hasError = !!errorMessage;

    return (
      <div className={cn('w-full', containerClassName)}>
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              'mb-1.5 block text-sm font-medium leading-6',
              hasError ? 'text-error-500' : 'text-neutral-900'
            )}
          >
            {label}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-neutral-400">
              {leftIcon}
            </div>
          )}

          {leftElement && (
            <div className="absolute inset-y-0 left-0 flex items-center pl-3">
              {leftElement}
            </div>
          )}

          <input
            id={inputId}
            ref={ref}
            className={cn(
              inputVariants({ variant, size, state: hasError ? 'error' : state }),
              leftIcon && 'pl-10',
              leftElement && 'pl-10',
              (rightIcon || rightElement) && 'pr-10',
              className
            )}
            aria-invalid={hasError}
            aria-describedby={hasError ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined}
            {...props}
          />

          {rightIcon && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-neutral-400">
              {rightIcon}
            </div>
          )}

          {rightElement && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              {rightElement}
            </div>
          )}
        </div>

        {hasError && (
          <p id={`${inputId}-error`} className="mt-1.5 text-sm text-error-500">
            {errorMessage}
          </p>
        )}

        {helperText && !hasError && (
          <p id={`${inputId}-helper`} className="mt-1.5 text-sm text-neutral-500">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
